import { useList } from "@refinedev/core";
import { useEffect, useState } from "react";
import {
  BookOpen,
  BookOpenCheck,
  BriefcaseBusiness,
  CalendarDays,
  Contact,
  FileText,
  LayoutDashboard,
  LogOut,
  ReceiptText,
  Search,
  Settings,
  UserPlus,
  UserRound,
} from "lucide-react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router";
import { apiFetch, ensureCsrf } from "@contractor-quoting/api-client";
import { Badge, Button, Card } from "@contractor-quoting/shared-ui";
import {
  ContractorMeasurements,
  type ContractorMeasurement,
  type ContractorSpace,
} from "../components/ContractorMeasurements";
import { CustomerDirectory } from "../components/CustomerDirectory.js";
import { ContractorProjectDossier } from "../components/ContractorProjectDossier.js";

const navigation = [
  [LayoutDashboard, "Dashboard", "/contractor/dashboard"],
  [Search, "Leads", "/contractor/leads"],
  [BriefcaseBusiness, "Projects", "/contractor/projects"],
  [Contact, "Customers", "/contractor/customers"],
  [ReceiptText, "Quotes", "/contractor/quotes"],
  [CalendarDays, "Schedule", "/contractor/schedule"],
  [FileText, "Files", "/contractor/files"],
  [BookOpen, "Runbooks", "/contractor/runbooks"],
  [BookOpenCheck, "Docs", "/contractor/docs"],
  [UserPlus, "Team", "/contractor/team"],
  [Settings, "Settings", "/contractor/settings"],
] as const;

export function ContractorLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [identity, setIdentity] = useState<{
    user?: { email: string };
    memberships?: Array<{ id: string; name: string; role: string }>;
    activeMembershipId?: string | null;
  } | null>(null);
  useEffect(() => {
    void ensureCsrf().catch(() => undefined);
    void apiFetch<typeof identity>("/v1/auth/me")
      .then(setIdentity)
      .catch(() => undefined);
  }, []);
  const logout = async () => {
    await apiFetch("/v1/auth/logout", { method: "POST" }).catch(() => undefined);
    void navigate("/login");
  };
  const activeMembership =
    identity?.memberships?.find((membership) => membership.id === identity.activeMembershipId) ??
    identity?.memberships?.[0];
  return (
    <div className="grid h-dvh min-h-0 grid-cols-1 overflow-hidden bg-stone-100 lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside className="hidden min-h-0 flex-col border-r border-stone-200 bg-emerald-950 p-5 text-white lg:flex">
        <Link to="/contractor/dashboard" className="flex items-center gap-3">
          <span className="h-11 w-11 overflow-hidden rounded-xl border border-white/15 bg-white shadow-sm">
            <img
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover"
              src="/brand/mziq-icon.png"
            />
          </span>
          <span>
            <span className="block text-xl font-black tracking-tight">Mezoniq</span>
            <span className="block text-xs font-semibold text-emerald-200">
              Contractor dashboard
            </span>
          </span>
        </Link>
        <nav className="mt-8 min-h-0 flex-1 overflow-y-auto">
          {navigation.map(([Icon, label, to]) => (
            <Link
              key={to}
              to={to}
              className={`flex items-center gap-3 rounded-xl px-4 py-3 font-semibold ${location.pathname.startsWith(to) ? "bg-white text-emerald-950" : "text-emerald-100 hover:bg-emerald-900"}`}
            >
              <Icon size={19} />
              {label}
            </Link>
          ))}
        </nav>
        <button
          className="mt-4 flex items-center gap-3 rounded-xl px-4 py-3 text-left font-semibold text-emerald-100 hover:bg-emerald-900"
          onClick={() => void logout()}
        >
          <LogOut size={19} /> Log out
        </button>
      </aside>
      <div className="flex h-full min-h-0 min-w-0 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-stone-200 bg-white px-5">
          <div>
            <p className="font-black text-emerald-950">
              {activeMembership?.name ?? "Contractor workspace"}
            </p>
            <p className="text-xs capitalize text-stone-500">{activeMembership?.role ?? ""}</p>
          </div>
          <div className="relative">
            <button
              className="flex items-center gap-2 rounded-xl border border-stone-200 px-3 py-2 font-semibold"
              onClick={() => setMenu(!menu)}
            >
              <UserRound size={19} />
              <span className="hidden sm:inline">{identity?.user?.email ?? "Account"}</span>
            </button>
            {menu ? (
              <div className="absolute right-0 z-20 mt-2 w-52 rounded-xl border bg-white p-2 shadow-xl">
                {(identity?.memberships?.length ?? 0) > 1 ? (
                  <div className="mb-2 border-b pb-2">
                    <p className="px-3 py-1 text-xs font-bold uppercase text-stone-400">
                      Switch workspace
                    </p>
                    {identity?.memberships?.map((membership) => (
                      <button
                        key={membership.id}
                        className={`w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-stone-100 ${identity.activeMembershipId === membership.id ? "font-black text-emerald-800" : ""}`}
                        onClick={() =>
                          void apiFetch("/v1/contractor/workspace", {
                            method: "POST",
                            body: JSON.stringify({ membershipId: membership.id }),
                          }).then(() => window.location.assign("/contractor/dashboard"))
                        }
                      >
                        {membership.name}
                      </button>
                    ))}
                  </div>
                ) : null}
                <Link
                  to="/contractor/settings"
                  className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-stone-100"
                >
                  <Settings size={17} /> My profile
                </Link>
                <button
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-stone-100"
                  onClick={() => void logout()}
                >
                  <LogOut size={17} /> Log out
                </button>
              </div>
            ) : null}
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const resourceTitle: Record<string, string> = {
  projects: "Project pipeline",
  customers: "Customers",
  "material-rules": "Material rules",
  "contractor-settings": "Contractor settings",
};

export function ResourceListPage({ resource }: { resource: string }) {
  const apiResource = resource === "contractor-settings" ? "settings" : resource;
  const { result, query } = useList({ resource: apiResource, pagination: { mode: "off" } });
  const items = (result.data ?? []) as Array<Record<string, unknown> & { id?: string }>;
  if (resource === "customers") {
    return (
      <>
        <div>
          <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
            Contractor dashboard
          </p>
          <h1 className="mt-2 text-4xl font-black text-emerald-950">Customers</h1>
        </div>
        {query.isLoading ? <p className="mt-8">Loading…</p> : null}
        {query.error ? <Card className="mt-8 text-red-800">{query.error.message}</Card> : null}
        <CustomerDirectory items={items} />
      </>
    );
  }
  return (
    <>
      <div>
        <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
          Contractor dashboard
        </p>
        <h1 className="mt-2 text-4xl font-black text-emerald-950">{resourceTitle[resource]}</h1>
      </div>
      {query.isLoading ? <p className="mt-8">Loading…</p> : null}
      {query.error ? <Card className="mt-8 text-red-800">{query.error.message}</Card> : null}
      <div className="mt-8 grid gap-4">
        {items.map((item, index) => (
          <Link
            key={item.id ?? index}
            to={resource === "projects" && item.id ? `/contractor/projects/${item.id}` : "#"}
          >
            <Card className="flex items-center justify-between hover:shadow-md">
              <div>
                <h2 className="font-bold">
                  {String(
                    item.name ??
                      item.title ??
                      item.display_name ??
                      item.customer_name ??
                      item.customer_email ??
                      item.email ??
                      item.project_type ??
                      `Record ${index + 1}`,
                  )}
                </h2>
                <p className="mt-1 text-sm text-stone-500">
                  {String(
                    (item.display_name ? item.email : item.phone) ??
                      item.updated_at ??
                      item.last_project_at ??
                      "",
                  )}
                </p>
              </div>
              {item.status ? <Badge>{String(item.status).replaceAll("_", " ")}</Badge> : null}
            </Card>
          </Link>
        ))}
        {items.length === 0 && !query.isLoading ? (
          <Card>
            <p className="text-stone-600">No records yet.</p>
          </Card>
        ) : null}
      </div>
    </>
  );
}

export function ContractorProjectDetail() {
  const { projectId: projectParam, leadId } = useParams();
  const projectId = leadId ?? projectParam ?? "";
  const isLeadView = Boolean(leadId);
  const navigate = useNavigate();
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [note, setNote] = useState("");
  const [quoteDescription, setQuoteDescription] = useState("Project work as reviewed");
  const [quotePrice, setQuotePrice] = useState("");
  const load = () =>
    Promise.all([
      apiFetch<Record<string, unknown>>(`/v1/contractor/projects/${projectId}`),
      apiFetch<Record<string, unknown>>(`/v2/contractor/projects/${projectId}/dossier`).catch(
        () => ({}),
      ),
    ]).then(([legacy, dossier]) => setData({ ...legacy, ...dossier }));
  useEffect(() => {
    void load();
  }, [projectId]);
  useEffect(() => {
    const project = data?.project as { job_status?: string } | undefined;
    if (
      isLeadView &&
      project?.job_status &&
      !["lead", "quote_awaiting_approval", "quote_sent"].includes(project.job_status)
    ) {
      void navigate(`/contractor/projects/${projectId}`, { replace: true });
    }
  }, [data, isLeadView, navigate, projectId]);
  if (!data) return <p>Loading project…</p>;
  const project = data.project as {
    title?: string;
    job_type_name?: string;
    job_status: string;
    payment_status: string;
    customer_name?: string;
    customer_email?: string;
    status: string;
  };
  const spaces = (data.spaces ?? []) as ContractorSpace[];
  const measurements = (data.measurements ?? []) as ContractorMeasurement[];
  const estimates = data.estimates as Array<{
    total_low_minor: string;
    total_high_minor: string;
    currency: string;
  }>;
  const images = data.images as Array<{ id: string; filename: string; readUrl: string }>;
  const notes = data.notes as Array<{ id: string; body: string; author_email: string }>;
  const serviceAddress = data.serviceAddress as null | {
    line1: string;
    line2: string | null;
    city: string;
    region: string;
    postal_code: string;
    country: string;
  };
  const finalQuotes = data.finalQuotes as Array<{
    id: string;
    quote_number: string;
    status: string;
    total_minor: string;
    currency: string;
  }>;
  const tradeScopes = (data.tradeScopes ?? []) as Array<{
    trade_code: string;
    coverage: "covered" | "uncovered";
  }>;
  const invoices = (data.invoices ?? []) as Array<{
    id: string;
    invoice_number: string;
    status: string;
    total_minor: string;
    currency: string;
  }>;
  const nextStatuses: Record<string, string[]> = {
    submitted: ["contractor_reviewing", "walkthrough_requested", "archived"],
    contractor_reviewing: ["walkthrough_requested", "declined", "archived"],
    walkthrough_requested: ["walkthrough_scheduled", "contractor_reviewing", "archived"],
    walkthrough_scheduled: ["walkthrough_completed", "declined", "archived"],
    walkthrough_completed: ["declined", "archived"],
    final_quote_issued: ["archived"],
  };
  const changeStatus = async (status: string) => {
    await apiFetch(`/v1/contractor/projects/${projectId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    await load();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await apiFetch(`/v1/contractor/projects/${projectId}/notes`, {
      method: "POST",
      body: JSON.stringify({ body: note }),
    });
    setNote("");
    await load();
  };
  const createQuote = async () => {
    const unitPriceMinor = Math.round(Number(quotePrice) * 100);
    if (!Number.isSafeInteger(unitPriceMinor) || unitPriceMinor < 0) return;
    await apiFetch(`/v1/contractor/projects/${projectId}/quotes`, {
      method: "POST",
      body: JSON.stringify({
        currency: "CAD",
        taxLabel: "HST",
        taxBasisPoints: 1300,
        scope: quoteDescription,
        terms: "Payment terms and schedule are subject to this fixed-price quote.",
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        lineItems: [
          {
            code: "project",
            description: quoteDescription,
            quantity: "1",
            unit: "fixed",
            unitPriceMinor,
            taxable: true,
          },
        ],
      }),
    });
    setQuotePrice("");
    await load();
  };
  const createQuoteFromEstimate = async () => {
    await apiFetch(`/v1/contractor/projects/${projectId}/quotes/from-estimate`, {
      method: "POST",
      body: JSON.stringify({
        scope: quoteDescription,
        terms: "Payment is due according to the stated payment schedule.",
      }),
    });
    await load();
  };
  const changeJobStatus = async (status: "in_progress" | "completed" | "cancelled") => {
    await apiFetch(`/v1/contractor/projects/${projectId}/job-status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    await load();
  };
  return (
    <div>
      <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
        {isLeadView ? "Lead intake" : "Project detail"}
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-black">
            {project.title || project.job_type_name || "Customer project"}
          </h1>
          <p className="mt-2 text-stone-500">
            {project.customer_name || project.customer_email || "Customer"}
          </p>
        </div>
        <div className="flex gap-2">
          <Badge>{(project.job_status ?? project.status).replaceAll("_", " ")}</Badge>
          <Badge>{(project.payment_status ?? "not_started").replaceAll("_", " ")}</Badge>
        </div>
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <ContractorProjectDossier projectId={projectId} dossier={data} />
        <ContractorMeasurements spaces={spaces} measurements={measurements} />
        <Card>
          <h2 className="text-xl font-bold">Estimate revisions</h2>
          <p className="mt-3 text-stone-600">{estimates.length} version(s)</p>
          <Button
            className="mt-4"
            onClick={() =>
              void apiFetch(`/v1/contractor/projects/${projectId}/revisions`, {
                method: "POST",
                body: JSON.stringify({ customerVisible: true }),
              }).then(load)
            }
          >
            Create revision
          </Button>
        </Card>
        <Card>
          <h2 className="text-xl font-bold">Pipeline status</h2>
          <div className="mt-4 grid gap-2">
            {(nextStatuses[project.status] ?? []).map((status) => (
              <Button key={status} onClick={() => void changeStatus(status)}>
                Move to {status.replaceAll("_", " ")}
              </Button>
            ))}
            {project.job_status === "quote_accepted" ? (
              <Button onClick={() => void changeJobStatus("in_progress")}>Start project</Button>
            ) : null}
            {project.job_status === "in_progress" ? (
              <Button onClick={() => void changeJobStatus("completed")}>Mark complete</Button>
            ) : null}
          </div>
        </Card>
        <Card>
          <h2 className="text-xl font-bold">Trade coverage</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {tradeScopes.map((scope) => (
              <Badge key={scope.trade_code}>
                {scope.trade_code.replaceAll("_", " ")} · {scope.coverage}
              </Badge>
            ))}
          </div>
        </Card>
        {serviceAddress ? (
          <Card>
            <h2 className="text-xl font-bold">Service address</h2>
            <address className="mt-3 not-italic text-stone-600">
              {serviceAddress.line1}
              {serviceAddress.line2 ? (
                <>
                  <br />
                  {serviceAddress.line2}
                </>
              ) : null}
              <br />
              {serviceAddress.city}, {serviceAddress.region} {serviceAddress.postal_code}
              <br />
              {serviceAddress.country}
            </address>
          </Card>
        ) : null}
        <Card>
          <h2 className="text-xl font-bold">Fixed-price quote</h2>
          <label className="mt-3 block text-sm font-bold">
            Scope
            <input
              className="mt-1 w-full rounded-xl border p-3 font-normal"
              value={quoteDescription}
              onChange={(event) => setQuoteDescription(event.target.value)}
            />
          </label>
          <label className="mt-3 block text-sm font-bold">
            Fixed price before HST (CAD)
            <input
              className="mt-1 w-full rounded-xl border p-3 font-normal"
              inputMode="decimal"
              value={quotePrice}
              onChange={(event) => setQuotePrice(event.target.value)}
            />
          </label>
          <Button
            className="mt-3"
            disabled={!quotePrice || !quoteDescription}
            onClick={() => void createQuote()}
          >
            Prepare draft
          </Button>
          {estimates.length > 0 ? (
            <Button className="ml-2 mt-3" onClick={() => void createQuoteFromEstimate()}>
              Draft from latest estimate
            </Button>
          ) : null}
          <div className="mt-4 divide-y">
            {finalQuotes.map((quote) => (
              <div className="flex items-center justify-between py-3" key={quote.id}>
                <div>
                  <p className="font-bold">{quote.quote_number}</p>
                  <p className="text-sm text-stone-500">
                    {new Intl.NumberFormat("en-CA", {
                      style: "currency",
                      currency: quote.currency,
                    }).format(Number(quote.total_minor) / 100)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge>{quote.status}</Badge>
                  {quote.status === "draft" ? (
                    <>
                      <a
                        className="rounded-lg border px-3 py-2 text-sm font-bold"
                        href={`/api/v1/contractor/quotes/${quote.id}/preview`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Preview PDF
                      </a>
                      <Button
                        onClick={() =>
                          void apiFetch(`/v1/contractor/quotes/${quote.id}/issue`, {
                            method: "POST",
                          }).then(load)
                        }
                      >
                        Approve & send
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          {project.job_status === "completed" &&
          finalQuotes.some((quote) => quote.status === "accepted") &&
          invoices.length === 0 ? (
            <Button
              className="mt-4"
              onClick={() =>
                void apiFetch(`/v1/contractor/projects/${projectId}/invoices`, {
                  method: "POST",
                  body: JSON.stringify({}),
                }).then(load)
              }
            >
              Issue invoice
            </Button>
          ) : null}
          {invoices.map((invoice) => (
            <div
              className="mt-3 flex items-center justify-between rounded-xl bg-stone-50 p-3"
              key={invoice.id}
            >
              <div>
                <p className="font-bold">{invoice.invoice_number}</p>
                <p className="text-sm text-stone-500">
                  {new Intl.NumberFormat("en-CA", {
                    style: "currency",
                    currency: invoice.currency,
                  }).format(Number(invoice.total_minor) / 100)}
                </p>
              </div>
              {invoice.status === "issued" ? (
                <Button
                  onClick={() =>
                    void apiFetch(`/v1/contractor/invoices/${invoice.id}/paid`, {
                      method: "POST",
                    }).then(load)
                  }
                >
                  Mark paid
                </Button>
              ) : (
                <Badge>{invoice.status}</Badge>
              )}
            </div>
          ))}
        </Card>
        <Card>
          <h2 className="text-xl font-bold">Internal notes</h2>
          <textarea
            className="mt-4 min-h-24 w-full rounded-xl border border-stone-300 bg-white p-3"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Visible only to contractor staff"
          />
          <Button className="mt-3" onClick={() => void addNote()}>
            Add note
          </Button>
          <div className="mt-4 grid gap-2">
            {notes.map((item) => (
              <div className="rounded-lg bg-stone-100 p-3 text-sm" key={item.id}>
                <p>{item.body}</p>
                <p className="mt-1 text-xs text-stone-500">{item.author_email}</p>
              </div>
            ))}
          </div>
        </Card>
        <Card className="lg:col-span-2">
          <h2 className="text-xl font-bold">Customer reference images</h2>
          <div className="mt-4 flex flex-wrap gap-4">
            {images.map((image) => (
              <a
                key={image.id}
                href={image.readUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border bg-stone-50 p-3 font-semibold"
              >
                {image.filename}
              </a>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
